import React, { useState, useEffect, useMemo, useCallback } from 'react';

// "Put this on the board" — the form an agent completes before a deal goes Available.
//
// Marking a deal Available publishes it to the whole org, and the sales side markets from exactly
// what is on the row, so the deal has to be complete first. The required list and the money model
// both live server-side in lib/availableRequirements.js; this form renders whatever the server says
// it needs (GET /api/deals/:id/available-requirements) rather than keeping its own copy, so the two
// cannot drift.
//
// Money is derived, never typed twice:
//     assignment fee = sales price − contract price
//     commission     = rate % of the CONTRACT price, or a flat amount
//     total fee      = commission + assignment fee
// The same arithmetic runs on the server on save; what is shown here is a live preview of it. The
// agent may type over the assignment fee for a deal with odd terms — that marks it as hand-set and
// stops it tracking the sales price.
//
// "Sales price" is deals.marketing_price — what WE sell at. It starts BLANK on purpose: the agent
// decides it. deals.asking_price (what the seller listed at) is loaded read-only and shown beside the
// box as a reference point, never pre-filled into it — they are different numbers (migration 076).
//
// Deals mirrored from the Google Sheet publish on their own terms and never reach this form.

const money = (n) => (n == null || n === '' || !Number.isFinite(Number(n)))
  ? '—' : `$${Math.round(Number(n)).toLocaleString()}`;

const num = (v) => (v === '' || v == null || !Number.isFinite(Number(v))) ? null : Number(v);

// Mirrors lib/availableRequirements.isFilled — 0 is a real answer, blank is not.
const filled = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (typeof v === 'number') return Number.isFinite(v);
  return true;
};

export default function MarkAvailableModal({ dealId, address, onClose, onPublished }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const [form, setForm]       = useState({});
  const [prefill, setPrefill] = useState({});
  const [applied, setApplied] = useState(false);   // has the listing prefill been accepted?
  const [touched, setTouched] = useState(false);   // has the agent tried to submit yet?
  const [feeManual, setFeeManual] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/deals/${dealId}/available-requirements`)
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (cancelled) return;
        if (!ok) { setError(d.error || 'Could not load this deal.'); setLoading(false); return; }
        const v = d.values || {};
        setForm(Object.fromEntries(Object.entries(v).map(([k, x]) => [k, x == null ? '' : x])));
        setPrefill(d.prefill || {});
        setFeeManual(v.assignment_fee_source === 'manual');
        setLoading(false);
      })
      .catch(() => { if (!cancelled) { setError('Could not load this deal.'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [dealId]);

  const set = useCallback((field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    if (field === 'assignment_fee') setFeeManual(true);
  }, []);

  // Live preview of the server's arithmetic.
  const calc = useMemo(() => {
    const contract = num(form.offer_amount);
    const sales    = num(form.marketing_price);
    const spread   = (contract == null || sales == null) ? null : Math.round(sales - contract);

    let commission = null;
    if (form.commission_type === 'none') commission = 0;
    else if (form.commission_type === 'flat') commission = num(form.commission_amount);
    else if (form.commission_type === 'percent') {
      const rate = num(form.commission_rate);
      commission = (rate == null || contract == null) ? null : Math.round(contract * (rate / 100));
    }

    const entered = num(form.assignment_fee);
    const assignment = feeManual ? entered : spread;
    const total = (commission == null || assignment == null) ? null : commission + assignment;
    return { spread, commission, assignment, total };
  }, [form, feeManual]);

  // Keep the assignment-fee box tracking the spread until the agent types over it.
  useEffect(() => {
    if (feeManual) return;
    const next = calc.spread == null ? '' : String(calc.spread);
    setForm(f => (String(f.assignment_fee ?? '') === next ? f : { ...f, assignment_fee: next }));
  }, [calc.spread, feeManual]);

  // What's still blank, checked the same way the server checks it.
  const missing = useMemo(() => {
    const out = [];
    const need = (field, label) => { if (!filled(form[field] === '' ? null : form[field])) out.push({ field, label }); };
    need('address', 'Street address'); need('city', 'City'); need('state', 'State'); need('zip', 'ZIP');
    need('beds', 'Beds'); need('baths', 'Baths'); need('garage', 'Garage spaces'); need('pool', 'Pool');
    need('sqft', 'Square feet'); need('lot_size', 'Lot size'); need('year_built', 'Year built');
    need('description', 'Description'); need('photos_link', 'Photos link');
    need('offer_amount', 'Contract price'); need('marketing_price', 'Sales price'); need('ai_arv', 'ARV');
    need('commission_type', 'Commission'); need('assignment_fee', 'Assignment fee');
    need('close_date', 'Close date'); need('inspection_end_date', 'Inspection period expires');
    need('title_company', 'Title company'); need('earnest_money', 'Earnest money (EMD)');
    if (form.commission_type === 'percent' && !filled(num(form.commission_rate))) out.push({ field: 'commission_rate', label: 'Commission rate (%)' });
    if (form.commission_type === 'flat' && !filled(num(form.commission_amount))) out.push({ field: 'commission_amount', label: 'Commission amount' });
    return out;
  }, [form]);

  const missingFields = useMemo(() => new Set(missing.map(m => m.field)), [missing]);
  const prefillCount = Object.keys(prefill).length;

  function applyPrefill() {
    setForm(f => {
      const next = { ...f };
      for (const [k, v] of Object.entries(prefill)) if (!filled(next[k] === '' ? null : next[k])) next[k] = v;
      return next;
    });
    setApplied(true);
  }

  // Take the agent to the first thing that's missing. Without this, "3 fields still needed" can name
  // two fields that are below the fold with nothing on screen pointing at them.
  function revealFirstMissing(list) {
    const first = list[0];
    if (!first) return;
    const host = document.querySelector(`[data-field="${first.field}"]`);
    if (!host) return;
    host.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const control = host.querySelector('input, textarea, select');
    if (control) control.focus({ preventScroll: true });
  }

  async function publish() {
    setTouched(true);
    if (missing.length) { setError(null); revealFirstMissing(missing); return; }
    setSaving(true); setError(null);
    try {
      const body = { stage: 'Available' };
      for (const k of ['address', 'city', 'state', 'zip', 'lot_size', 'description', 'title_company',
        'photos_link',
        'beds', 'baths', 'garage', 'pool', 'sqft', 'year_built', 'earnest_money',
        'offer_amount', 'marketing_price', 'ai_arv',
        'close_date', 'inspection_end_date', 'commission_type', 'commission_rate', 'commission_amount',
        'assignment_fee']) {
        body[k] = form[k] === '' ? null : form[k];
      }
      if (feeManual) body.assignment_fee_source = 'manual';

      const r = await fetch(`/api/deals/${dealId}/stage`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) {
        // The server rejected it — show exactly which fields it wants.
        if (d.missing?.length) {
          setError(`Still needed: ${d.missing.map(m => m.label).join(', ')}`);
          revealFirstMissing(d.missing);
        }
        else setError(d.error || 'Could not publish this deal.');
        setSaving(false);
        return;
      }
      if (onPublished) onPublished(d);
      onClose();
    } catch (e) {
      setError(e.message || 'Could not publish this deal.');
      setSaving(false);
    }
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={panel} onClick={e => e.stopPropagation()}>

        <div style={head}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Put this deal on the board</div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {address || `Deal #${dealId}`} — the whole team sees this once it's Available.
            </div>
          </div>
          <button onClick={onClose} style={closeBtn} aria-label="Close">×</button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>Loading the deal…</div>
          ) : (
            <div style={{ padding: '16px 20px 20px' }}>

              {/* Offer to lift what the listing already states, rather than making them retype it. */}
              {prefillCount > 0 && !applied && (
                <div style={prefillBar}>
                  <span style={{ flex: 1 }}>
                    The MLS listing has {prefillCount} of these field{prefillCount === 1 ? '' : 's'} already
                    — {Object.keys(prefill).join(', ')}.
                  </span>
                  <button onClick={applyPrefill} style={btnSmall}>Fill from listing</button>
                </div>
              )}
              {applied && (
                <div style={{ ...prefillBar, background: 'var(--teal-dim)', borderColor: 'rgba(20,80,74,0.3)' }}>
                  Filled from the listing — check them over, they're yours to correct.
                </div>
            )}

              <Section title="Property">
                <Row>
                  <Field field="address" label="Street address" span={2} bad={touched && missingFields.has('address')}>
                    <input style={inp} value={form.address ?? ''} onChange={e => set('address', e.target.value)} placeholder="4412 W Cheryl Dr" />
                  </Field>
                  <Field field="city" label="City" bad={touched && missingFields.has('city')}>
                    <input style={inp} value={form.city ?? ''} onChange={e => set('city', e.target.value)} />
                  </Field>
                </Row>
                <Row>
                  <Field field="state" label="State" bad={touched && missingFields.has('state')}>
                    <input style={inp} value={form.state ?? ''} onChange={e => set('state', e.target.value)} maxLength={2} placeholder="AZ" />
                  </Field>
                  <Field field="zip" label="ZIP" bad={touched && missingFields.has('zip')}>
                    <input style={inp} value={form.zip ?? ''} onChange={e => set('zip', e.target.value)} placeholder="85302" />
                  </Field>
                  <Field field="title_company" label="Title company" bad={touched && missingFields.has('title_company')}>
                    <input style={inp} value={form.title_company ?? ''} onChange={e => set('title_company', e.target.value)} placeholder="Magnus Title" />
                  </Field>
                </Row>
              </Section>

              <Section title="Specs" note="What the sales side puts in front of a buyer">
                <Row>
                  <Field field="beds" label="Beds" bad={touched && missingFields.has('beds')}>
                    <input style={inp} type="number" min="0" value={form.beds ?? ''} onChange={e => set('beds', e.target.value)} />
                  </Field>
                  <Field field="baths" label="Baths" bad={touched && missingFields.has('baths')}>
                    <input style={inp} type="number" min="0" step="0.5" value={form.baths ?? ''} onChange={e => set('baths', e.target.value)} />
                  </Field>
                  <Field field="garage" label="Garage spaces" bad={touched && missingFields.has('garage')}>
                    <input style={inp} type="number" min="0" value={form.garage ?? ''} onChange={e => set('garage', e.target.value)} />
                  </Field>
                </Row>
                <Row>
                  <Field field="sqft" label="Square feet" bad={touched && missingFields.has('sqft')}>
                    <input style={inp} type="number" min="0" value={form.sqft ?? ''} onChange={e => set('sqft', e.target.value)} />
                  </Field>
                  <Field field="lot_size" label="Lot size" bad={touched && missingFields.has('lot_size')}>
                    <input style={inp} value={form.lot_size ?? ''} onChange={e => set('lot_size', e.target.value)} placeholder="7,200 sqft" />
                  </Field>
                  <Field field="year_built" label="Year built" bad={touched && missingFields.has('year_built')}>
                    <input style={inp} type="number" min="1800" max="2100" value={form.year_built ?? ''} onChange={e => set('year_built', e.target.value)} placeholder="1978" />
                  </Field>
                </Row>
                <Row>
                  <Field field="pool" label="Pool" bad={touched && missingFields.has('pool')}>
                    <select style={inp} value={form.pool === '' || form.pool == null ? '' : String(form.pool)} onChange={e => set('pool', e.target.value === '' ? '' : Number(e.target.value))}>
                      <option value="">Select…</option>
                      <option value="1">Yes</option>
                      <option value="0">No</option>
                    </select>
                  </Field>
                </Row>
                <Row>
                  <Field field="photos_link" label="Photos link" span={3} hint="Your own photo gallery — Dropbox, Google Drive, anything buyers can open. This is the “Pics:” line in the pitch."
                    bad={touched && missingFields.has('photos_link')}>
                    <input style={inp} type="url" value={form.photos_link ?? ''} onChange={e => set('photos_link', e.target.value)}
                      placeholder="https://drive.google.com/drive/folders/…" />
                  </Field>
                </Row>
                <Row>
                  <Field field="description" label="Description" span={3} bad={touched && missingFields.has('description')}>
                    <textarea style={{ ...inp, minHeight: 68, resize: 'vertical', lineHeight: 1.45 }}
                      value={form.description ?? ''} onChange={e => set('description', e.target.value)}
                      placeholder="Condition, access, why it's a deal — this is what buyers read." />
                  </Field>
                </Row>
              </Section>

              <Section title="Money" note="Contract price stays private to you and the org owner">
                <Row>
                  <Field field="offer_amount" label="Contract price" hint="Front-side — what you signed with the seller" bad={touched && missingFields.has('offer_amount')}>
                    <input style={inp} type="number" min="0" value={form.offer_amount ?? ''} onChange={e => set('offer_amount', e.target.value)} />
                  </Field>
                  <Field field="marketing_price" label="Sales price"
                    hint={form.asking_price ? `You choose this — seller listed at ${money(num(form.asking_price))}` : "What you're selling it for — your call"}
                    bad={touched && missingFields.has('marketing_price')}>
                    <input style={inp} type="number" min="0" value={form.marketing_price ?? ''} onChange={e => set('marketing_price', e.target.value)} placeholder="Your sell price" />
                  </Field>
                  <Field field="ai_arv" label="ARV" bad={touched && missingFields.has('ai_arv')}>
                    <input style={inp} type="number" min="0" value={form.ai_arv ?? ''} onChange={e => set('ai_arv', e.target.value)} />
                  </Field>
                </Row>
                <Row>
                  <Field field="commission_type" label="Commission from seller" bad={touched && missingFields.has('commission_type')}>
                    <select style={inp} value={form.commission_type ?? ''} onChange={e => set('commission_type', e.target.value)}>
                      <option value="">Select…</option>
                      <option value="none">No commission</option>
                      <option value="percent">Percentage</option>
                      <option value="flat">Flat fee</option>
                    </select>
                  </Field>
                  {form.commission_type === 'percent' && (
                    <Field field="commission_rate" label="Rate (%)" hint="of the contract price" bad={touched && missingFields.has('commission_rate')}>
                      <input style={inp} type="number" min="0" step="0.25" value={form.commission_rate ?? ''} onChange={e => set('commission_rate', e.target.value)} placeholder="3" />
                    </Field>
                  )}
                  {form.commission_type === 'flat' && (
                    <Field field="commission_amount" label="Commission amount" bad={touched && missingFields.has('commission_amount')}>
                      <input style={inp} type="number" min="0" value={form.commission_amount ?? ''} onChange={e => set('commission_amount', e.target.value)} />
                    </Field>
                  )}
                  <Field field="assignment_fee" label="Assignment fee" hint={feeManual ? 'Hand-set — won\'t follow the sales price' : 'Sales price − contract price'} bad={touched && missingFields.has('assignment_fee')}>
                    <input style={inp} type="number" value={form.assignment_fee ?? ''} onChange={e => set('assignment_fee', e.target.value)} />
                  </Field>
                </Row>

                {/* The arithmetic, shown rather than asserted. */}
                <div style={mathBox}>
                  <MathRow label="Sales price" value={money(num(form.marketing_price))} />
                  <MathRow label="− Contract price" value={money(num(form.offer_amount))} />
                  <MathRow label="Assignment fee" value={money(calc.assignment)} rule
                    note={feeManual ? 'hand-set' : null} />
                  <MathRow label={form.commission_type === 'percent' && num(form.commission_rate) != null
                    ? `+ Commission (${num(form.commission_rate)}% of contract price)` : '+ Commission'}
                    value={money(calc.commission)} />
                  <MathRow label="Total company fee" value={money(calc.total)} rule strong />
                  {feeManual && (
                    <button onClick={() => { setFeeManual(false); }} style={{ ...btnSmall, marginTop: 8, alignSelf: 'flex-start' }}>
                      Reset to the calculated fee
                    </button>
                  )}
                </div>
              </Section>

              <Section title="Closing terms" note="These are the pitch's EMD and COE lines">
                <Row>
                  <Field field="close_date" label="Close date" bad={touched && missingFields.has('close_date')}>
                    <input style={inp} type="date" value={String(form.close_date ?? '').slice(0, 10)} onChange={e => set('close_date', e.target.value)} />
                  </Field>
                  <Field field="inspection_end_date" label="Inspection period expires" hint="Feeds the deadline countdown" bad={touched && missingFields.has('inspection_end_date')}>
                    <input style={inp} type="date" value={String(form.inspection_end_date ?? '').slice(0, 10)} onChange={e => set('inspection_end_date', e.target.value)} />
                  </Field>
                  <Field field="earnest_money" label="Earnest money (EMD)" bad={touched && missingFields.has('earnest_money')}>
                    <input style={inp} type="number" min="0" value={form.earnest_money ?? ''} onChange={e => set('earnest_money', e.target.value)} placeholder="1000" />
                  </Field>
                </Row>
              </Section>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={foot}>
          <div style={{ fontSize: 12, flex: 1, minWidth: 0 }}>
            {error ? (
              <span style={{ color: 'var(--red)', fontWeight: 600 }}>{error}</span>
            ) : missing.length ? (
              <span style={{ color: touched ? 'var(--red)' : 'var(--gray-500)' }}>
                {missing.length} field{missing.length === 1 ? '' : 's'} still needed
                {touched ? `: ${missing.map(m => m.label).join(', ')}` : ''}
              </span>
            ) : (
              <span style={{ color: 'var(--teal)', fontWeight: 600 }}>Ready — everything the team needs is filled in.</span>
            )}
          </div>
          <button onClick={onClose} style={ghostBtn}>Cancel</button>
          <button onClick={publish} disabled={saving || loading}
            style={{ ...primaryBtn, opacity: (saving || loading) ? 0.55 : 1, cursor: (saving || loading) ? 'default' : 'pointer' }}>
            {saving ? 'Publishing…' : 'Publish to board'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Layout bits ───────────────────────────────────────────────────────────────
function Section({ title, note, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 9 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-300)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{title}</div>
        {note && <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{note}</div>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{children}</div>
    </div>
  );
}
const Row = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 }}>{children}</div>
);
function Field({ label, hint, span = 1, bad, field, children }) {
  return (
    <div data-field={field} style={{ gridColumn: span > 1 ? `span ${span}` : undefined, minWidth: 0 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: bad ? 'var(--red)' : 'var(--gray-300)', marginBottom: 4 }}>
        {label}{bad ? ' *' : ''}
      </label>
      {children}
      {hint && <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 3 }}>{hint}</div>}
    </div>
  );
}
function MathRow({ label, value, rule, strong, note }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', gap: 10, fontSize: strong ? 14 : 12.5,
      fontWeight: strong ? 700 : 400, color: strong ? 'var(--white)' : 'var(--gray-300)',
      paddingTop: rule ? 7 : 0, marginTop: rule ? 5 : 0,
      borderTop: rule ? '1px solid var(--navy-border)' : 'none',
    }}>
      <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
      {note && <span style={{ fontSize: 10.5, color: 'var(--gold)', fontWeight: 600 }}>{note}</span>}
      <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: strong ? 700 : 600, color: 'var(--white)' }}>{value}</span>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(26, 23, 20, 0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
};
const panel = {
  background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14,
  width: 'min(760px, 100%)', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
  color: 'var(--white)', boxShadow: '0 20px 60px rgba(20,30,26,0.25)',
};
const head = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
  padding: '18px 20px', borderBottom: '1px solid var(--navy-border)',
};
const foot = {
  display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px',
  borderTop: '1px solid var(--navy-border)', background: 'var(--navy)',
  borderRadius: '0 0 14px 14px', flexWrap: 'wrap',
};
const inp = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8,
  border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)',
  fontSize: 13, fontFamily: 'inherit',
};
const closeBtn = {
  background: 'transparent', border: 'none', color: 'var(--gray-500)', fontSize: 22,
  lineHeight: 1, cursor: 'pointer', padding: 0, flexShrink: 0,
};
const ghostBtn = {
  padding: '8px 14px', borderRadius: 8, background: 'transparent',
  border: '1px solid var(--navy-border)', color: 'var(--gray-300)', fontSize: 13, fontWeight: 600,
};
const primaryBtn = {
  padding: '8px 16px', borderRadius: 8, background: 'var(--teal)', border: '1px solid var(--teal)',
  color: '#fff', fontSize: 13, fontWeight: 700,
};
const btnSmall = {
  padding: '5px 11px', borderRadius: 7, background: 'var(--navy-card)',
  border: '1px solid var(--navy-border)', color: 'var(--teal)', fontSize: 11.5, fontWeight: 700,
  cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit',
};
// The arithmetic, shown rather than asserted — the agent can see where the total came from.
const mathBox = {
  display: 'flex', flexDirection: 'column', gap: 3, marginTop: 4, padding: '12px 14px',
  borderRadius: 9, background: 'var(--navy)', border: '1px solid var(--navy-border)',
};
const prefillBar = {
  display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, padding: '9px 12px',
  borderRadius: 9, background: 'var(--gold-dim)', border: '1px solid rgba(224,165,38,0.3)',
  fontSize: 12, color: 'var(--gray-100)',
};
